import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';

export interface ProgressItem {
    id: string;
    type: 'upload' | 'download' | 'processing' | 'other';
    filename: string;
    progress: number;
    status: 'initiated' | 'uploading' | 'processing' | 'completed' | 'error' | 'interrupted' | 'resumed' | 'resuming';
    eta?: number;
    speed?: number;
    error?: string;
    metadata?: any;
}

// 1. Items Context (Fast updates: 500ms) - Only for the Widget
interface ProgressItemsContextType {
    progressItems: Record<string, ProgressItem>;
}

// 2. UI Context (Slow updates) - For Modals/Global UI
interface ProgressUIContextType {
    isMinimized: boolean;
    showModal: boolean;
    isModalOpen: boolean;
}

// 3. Actions Context (Stable) - For Hooks
interface ProgressActionsContextType {
    addProgress: (id: string, item: Omit<ProgressItem, 'id'>) => void;
    updateProgress: (id: string, updates: Partial<ProgressItem>) => void;
    removeProgress: (id: string) => void;
    clearCompleted: () => void;
    clearAll: () => void;
    setIsMinimized: (minimized: boolean) => void;
    setShowModal: (show: boolean) => void;
    setIsModalOpen: (open: boolean) => void;
}

const ProgressItemsContext = createContext<ProgressItemsContextType | undefined>(undefined);
const ProgressUIContext = createContext<ProgressUIContextType | undefined>(undefined);
const ProgressActionsContext = createContext<ProgressActionsContextType | undefined>(undefined);

export const ProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [progressItems, setProgressItems] = useState<Record<string, ProgressItem>>({});
    const [isMinimized, setIsMinimized] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const addProgress = useCallback((id: string, item: Omit<ProgressItem, 'id'>) => {
        if (!id) {
            console.warn('addProgress called with invalid id');
            return;
        }

        if (!item || typeof item !== 'object') {
            console.warn('addProgress called with invalid item');
            return;
        }

        setProgressItems(prev => {
            if (!prev) {
                return { [id]: { ...item, id } };
            }

            return {
                ...prev,
                [id]: { ...item, id }
            };
        });
    }, []);

    const updateProgress = useCallback((id: string, updates: Partial<ProgressItem>) => {
        if (!id) {
            console.warn('updateProgress called with invalid id');
            return;
        }

        if (!updates || typeof updates !== 'object') {
            console.warn('updateProgress called with invalid updates');
            return;
        }

        setProgressItems(prev => {
            if (!prev || !prev[id]) {
                console.warn(`Progress item with id ${id} not found`);
                return prev;
            }

            const item = prev[id];
            if (!item) return prev;

            let changed = false;
            for (const key in updates) {
                if (!Object.prototype.hasOwnProperty.call(updates, key)) continue;

                const val = (updates as any)[key];
                const current = (item as any)[key];

                // For progress, only trigger update if it changed by at least 1%
                if (key === 'progress' && typeof val === 'number' && typeof current === 'number') {
                    if (Math.abs(val - current) < 1 && val < 100) continue;
                }

                // Check for actual changes (including null/undefined handling)
                if (val !== current && (val !== undefined || current !== undefined)) {
                    changed = true;
                    break;
                }
            }
            
            if (!changed) return prev;

            return {
                ...prev,
                [id]: { ...prev[id], ...updates }
            };
        });
    }, []);

    const removeProgress = useCallback((id: string) => {
        if (!id) {
            console.warn('removeProgress called with invalid id');
            return;
        }

        setProgressItems(prev => {
            if (!prev || !prev[id]) {
                return prev;
            }

            const newItems = { ...prev };
            delete newItems[id];
            return newItems;
        });
    }, []);

    const clearCompleted = useCallback(() => {
        setProgressItems(prev => {
            if (!prev || Object.keys(prev).length === 0) {
                return prev;
            }

            const newItems: Record<string, ProgressItem> = {};
            let changed = false;
            
            Object.entries(prev).forEach(([id, item]) => {
                if (!item || !item.status) {
                    // Keep items with invalid status
                    newItems[id] = item;
                    return;
                }

                if (item.status !== 'completed' && item.status !== 'error') {
                    newItems[id] = item;
                } else {
                    changed = true;
                }
            });
            
            return changed ? newItems : prev;
        });
    }, []);

    const clearAll = useCallback(() => {
        setProgressItems({});
    }, []);

    const actions = useMemo(() => ({
        addProgress,
        updateProgress,
        removeProgress,
        clearCompleted,
        clearAll,
        setIsMinimized,
        setShowModal,
        setIsModalOpen
    }), [addProgress, updateProgress, removeProgress, clearCompleted, clearAll]);

    // Fast changing state
    const itemsState = useMemo(() => ({
        progressItems
    }), [progressItems]);

    // Slow changing state
    const uiState = useMemo(() => ({
        isMinimized,
        showModal,
        isModalOpen
    }), [isMinimized, showModal, isModalOpen]);

    return (
        <ProgressActionsContext.Provider value={actions}>
            <ProgressUIContext.Provider value={uiState}>
                <ProgressItemsContext.Provider value={itemsState}>
                    {children}
                </ProgressItemsContext.Provider>
            </ProgressUIContext.Provider>
        </ProgressActionsContext.Provider>
    );
};

// Hook for Widget ONLY (Fast updates)
export const useProgressItems = () => {
    const context = useContext(ProgressItemsContext);
    if (!context) {
        throw new Error('useProgressItems must be used within a ProgressProvider');
    }
    return {
        progressItems: context.progressItems || {}
    };
};

// Hook for Modals/Pages (Slow updates)
export const useProgressUI = () => {
    const context = useContext(ProgressUIContext);
    if (!context) {
        throw new Error('useProgressUI must be used within a ProgressProvider');
    }
    return {
        isMinimized: context.isMinimized ?? false,
        showModal: context.showModal ?? false,
        isModalOpen: context.isModalOpen ?? false
    };
};

// Hook for Actions (No updates)
export const useProgressActions = () => {
    const context = useContext(ProgressActionsContext);
    if (!context) {
        throw new Error('useProgressActions must be used within a ProgressProvider');
    }
    return context;
};

// Legacy hook (Avoiding breakage, but usage causes re-renders)
export const useProgress = () => {
    const items = useProgressItems();
    const ui = useProgressUI();
    const actions = useProgressActions();
    return useMemo(() => ({ ...items, ...ui, ...actions }), [items, ui, actions]);
};
